import React from "react";
import {
    FaArrowLeft, FaCalendarAlt, FaPhoneAlt, FaMapMarkerAlt,
    FaFileInvoice, FaCheckCircle, FaPalette, FaRuler, FaClock,
    FaEnvelope, FaTruck, FaShieldAlt, FaImage
} from "react-icons/fa";
import { myCustomTheme } from "../../Theme";
import { MASTER_COLOR_PALETTE } from "../../Pages/OurProduct/constants";
import { getDynamicPrices } from "../../Pages/OurProduct/utils/priceUtils";
import { getShippingCharge } from "../../Pages/OurProduct/utils/shippingUtils";
import { generateInvoicePDF } from "../../Pages/OurProduct/utils/orderUtils";
import "./style/OrderSuccess.css";

// ─── Helper: price per cart item (same as PaymentPage) ────────
const getItemDisplayPrice = (item) => {
    if (item.type === "Customize") {
        return Math.round((item.sellingPrice || 0) * 6 * 1.18);
    }
    return Math.round((item.sellingPrice || 0) * 1.18);
};

const OrderSuccess = ({
    selectedProduct,
    userDetails,
    currentOrderId,
    invoiceNumber,
    deliveryDate,
    deliveryTime,
    onBack
}) => {
    // ── Detect Buy All ───────────────────────────────────────────
    const isBuyAll   = Boolean(selectedProduct?.cartItems && selectedProduct.cartItems.length > 0);
    const cartItems  = isBuyAll ? selectedProduct.cartItems  : null;
    const cartImages = isBuyAll ? (selectedProduct.cartImages || {}) : {};

    // ── Single-product fields (only used when !isBuyAll) ─────────
    const isCustomized      = !isBuyAll && selectedProduct?.type === "Customize";
    const activeColorObject = !isBuyAll ? MASTER_COLOR_PALETTE.find(c => c.id === userDetails?.color) : null;
    const resolvedColorName = activeColorObject ? activeColorObject.name : "";
    const selectedInch      = userDetails?.selectedInch || "6";
    const dynamicPrices     = !isBuyAll ? getDynamicPrices(selectedProduct, selectedInch, userDetails?.quantity || 1) : null;

    // ── Product total ────────────────────────────────────────────
    let productTotal = 0;
    if (isBuyAll) {
        productTotal = cartItems.reduce((sum, item) => sum + getItemDisplayPrice(item), 0);
    } else if (selectedProduct) {
        productTotal = isCustomized
            ? (dynamicPrices?.sp || 0)
            : Math.round((selectedProduct.sellingPrice || 0) * (userDetails?.quantity || 1) * 1.18);
    }

    // ── Shipping & grand total ───────────────────────────────────
    const shippingInfo   = getShippingCharge(userDetails?.address || "");
    const shippingCharge = shippingInfo.charge;
    const grandTotal     = productTotal + shippingCharge;

    // ── GST breakdown (informational) ────────────────────────────
    const productBase = Math.round(productTotal / 1.18);
    const cgst        = Math.round(productBase * 9 / 100);
    const sgst        = Math.round(productBase * 9 / 100);

    // ── Invoice download ─────────────────────────────────────────
    const handleDownloadInvoice = () => {
        generateInvoicePDF({
            orderId:        currentOrderId,
            invoiceNumber:  invoiceNumber,
            name:           userDetails?.name   || "",
            email:          userDetails?.email  || "",
            phone:          userDetails?.phone  || "",
            address:        userDetails?.address || "",
            productName:    isBuyAll
                ? cartItems.map(i => i.name).join(", ")
                : (selectedProduct?.name || ""),
            productImage:   isBuyAll
                ? (cartItems[0]?.images?.[0] || "")
                : (selectedProduct?.images?.[0] || ""),
            colorName:      resolvedColorName,
            selectedInch:   isCustomized ? selectedInch : null,
            quantity:       userDetails?.quantity || 1,
            productTotal:   productTotal,
            shippingCharge: shippingCharge,
            shippingZone:   shippingInfo.label,
            totalAmount:    grandTotal,
            deliveryDate:   deliveryDate,
            deliveryTime:   deliveryTime || "6:00 PM",
            date:           new Date().toLocaleDateString("en-IN", {
                day: "numeric", month: "long", year: "numeric"
            }),
            isCustomized: isCustomized
        });
    };

    return (
        <div
            className="order-success-page"
            style={{
                backgroundColor: myCustomTheme.colors.header_footer_bg,
                boxShadow: myCustomTheme.shadows.soft,
                borderRadius: "24px",
                border: `1px solid ${myCustomTheme.colors.high_light}`
            }}
        >
            <button
                className="back-btn"
                onClick={onBack}
                style={{
                    backgroundColor: myCustomTheme.colors.button_bg,
                    color: myCustomTheme.colors.button_text
                }}
            >
                <FaArrowLeft /> Back to Products
            </button>

            {/* ── Success header ── */}
            <div style={{
                display: "flex", flexDirection: "column",
                alignItems: "center", padding: "28px 0 12px", gap: "10px"
            }}>
                <FaCheckCircle style={{ fontSize: "52px", color: "#16a34a" }} />
                <h1 style={{ color: myCustomTheme.colors.heading, margin: 0, textAlign: "center" }}>
                    Order Confirmed!
                </h1>
                <p style={{ color: myCustomTheme.colors.menu_text, margin: 0 }}>
                    Thank you for your order. We will contact you shortly.
                </p>
            </div>

            <div
                className="track-box"
                style={{
                    backgroundColor: myCustomTheme.colors.bg_primary,
                    borderColor: myCustomTheme.colors.high_light,
                    borderRadius: "16px",
                    padding: "24px"
                }}
            >
                {/* ══════════════════════════════════════════════
                    BUY ALL: Show every cart item with its price
                    ══════════════════════════════════════════════ */}
                {isBuyAll && (
                    <div style={{ marginBottom: "20px" }}>
                        <p style={{
                            margin: "0 0 12px 0", fontWeight: "700", fontSize: "15px",
                            color: myCustomTheme.colors.heading
                        }}>
                            {cartItems.length} Item{cartItems.length !== 1 ? "s" : ""} Ordered
                        </p>

                        {cartItems.map((item) => {
                            const itemPrice   = getItemDisplayPrice(item);
                            const isCustom    = item.type === "Customize";
                            const designImage = cartImages[item.id];

                            return (
                                <div
                                    key={item.id}
                                    style={{
                                        display: "flex", gap: "14px",
                                        alignItems: "flex-start",
                                        padding: "14px 0",
                                        borderBottom: `1px solid ${myCustomTheme.colors.high_light}`
                                    }}
                                >
                                    {/* Product image */}
                                    {item.images && item.images[0] && (
                                        <img
                                            src={item.images[0]}
                                            alt={item.name}
                                            style={{
                                                width: "60px", height: "60px",
                                                objectFit: "cover", borderRadius: "10px",
                                                border: `1.5px solid ${myCustomTheme.colors.high_light}`,
                                                flexShrink: 0
                                            }}
                                        />
                                    )}

                                    <div style={{ flex: 1 }}>
                                        <p style={{ margin: "0 0 2px 0", fontWeight: "700", fontSize: "14px", color: myCustomTheme.colors.heading }}>
                                            {item.name}
                                        </p>
                                        <p style={{ margin: "0 0 6px 0", fontSize: "12px", color: myCustomTheme.colors.menu_text }}>
                                            {item.type}{isCustom ? " · 6\"" : ""}
                                        </p>

                                        {/* Custom design thumbnail (Customize items only) */}
                                        {isCustom && designImage && (
                                            <div style={{
                                                display: "flex", alignItems: "center", gap: "8px",
                                                padding: "8px 10px",
                                                background: "#fefce8",
                                                border: "1.5px solid #fde68a",
                                                borderRadius: "8px"
                                            }}>
                                                <FaImage style={{ color: "#d97706", fontSize: "11px", flexShrink: 0 }} />
                                                <img
                                                    src={designImage}
                                                    alt="Custom Design"
                                                    style={{
                                                        width: "44px", height: "44px",
                                                        objectFit: "cover", borderRadius: "6px",
                                                        border: "2px solid #fcd34d",
                                                        cursor: "pointer"
                                                    }}
                                                    onClick={() => window.open(designImage, "_blank")}
                                                    title="Click to view full size"
                                                />
                                                <span style={{ fontSize: "11px", fontWeight: "700", color: "#92400e" }}>
                                                    Custom design ✓
                                                </span>
                                            </div>
                                        )}
                                    </div>

                                    {/* Item price */}
                                    <span style={{
                                        fontWeight: "800", fontSize: "15px",
                                        color: myCustomTheme.colors.heading,
                                        whiteSpace: "nowrap", alignSelf: "center"
                                    }}>
                                        ₹{itemPrice}
                                    </span>
                                </div>
                            );
                        })}
                    </div>
                )}

                {/* ══════════════════════════════════════════════
                    SINGLE PRODUCT: show product image + info
                    ══════════════════════════════════════════════ */}
                {!isBuyAll && selectedProduct?.images?.[0] && (
                    <div style={{ marginBottom: "20px", display: "flex", alignItems: "center", gap: "16px" }}>
                        <img
                            src={selectedProduct.images[0]}
                            alt={selectedProduct.name}
                            style={{
                                width: "72px", height: "72px",
                                objectFit: "cover", borderRadius: "12px",
                                border: `2px solid ${myCustomTheme.colors.high_light}`
                            }}
                        />
                        <div>
                            <p style={{ margin: 0, fontWeight: "700", fontSize: "16px", color: myCustomTheme.colors.heading }}>
                                {selectedProduct.name}
                            </p>
                            {resolvedColorName && (
                                <p style={{ margin: "4px 0 0 0", fontSize: "13px", color: myCustomTheme.colors.menu_text, display: "flex", alignItems: "center", gap: "5px" }}>
                                    <FaPalette /> Color: {resolvedColorName}
                                </p>
                            )}
                            {isCustomized && (
                                <p style={{ margin: "4px 0 0 0", fontSize: "13px", color: myCustomTheme.colors.menu_text, display: "flex", alignItems: "center", gap: "5px" }}>
                                    <FaRuler /> Size: {selectedInch} inches
                                </p>
                            )}
                        </div>
                    </div>
                )}

                {/* ── Delivery date badge ── */}
                <div style={{
                    background: "linear-gradient(135deg, #dcfce7, #d1fae5)",
                    border: "1.5px solid #86efac",
                    borderRadius: "12px",
                    padding: "16px 20px",
                    marginBottom: "20px",
                    display: "flex", alignItems: "flex-start", gap: "12px"
                }}>
                    <FaCalendarAlt style={{ color: "#16a34a", fontSize: "22px", marginTop: "2px", flexShrink: 0 }} />
                    <div>
                        <p style={{ margin: 0, fontSize: "12px", fontWeight: "700", color: "#14532d", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                            Estimated Delivery
                        </p>
                        <p style={{ margin: "4px 0 2px 0", fontSize: "16px", fontWeight: "800", color: "#15803d" }}>
                            {deliveryDate}
                        </p>
                        <p style={{ margin: 0, fontSize: "13px", color: "#166534", display: "flex", alignItems: "center", gap: "5px" }}>
                            <FaClock style={{ fontSize: "11px" }} />
                            by {deliveryTime || "6:00 PM"}
                            {!isBuyAll && isCustomized && (
                                <span style={{ marginLeft: "8px", fontSize: "11px", fontStyle: "italic" }}>
                                    (Customized: 2 extra days)
                                </span>
                            )}
                        </p>
                    </div>
                </div>

                {/* ── Order info + Price breakdown ── */}
                <div style={{ display: "flex", flexDirection: "column", gap: "8px", marginBottom: "20px" }}>

                    {/* Invoice number */}
                    {invoiceNumber && (
                        <div style={{
                            display: "flex", justifyContent: "space-between",
                            padding: "10px 14px",
                            backgroundColor: "#eff6ff",
                            borderRadius: "8px",
                            border: "1px solid #bfdbfe"
                        }}>
                            <span style={{ color: "#1d4ed8", fontSize: "13px", fontWeight: "600", display: "flex", alignItems: "center", gap: "5px" }}>
                                <FaFileInvoice /> Invoice No.
                            </span>
                            <span style={{ fontWeight: "800", color: "#1e40af", fontSize: "13px", fontFamily: "monospace" }}>
                                {invoiceNumber}
                            </span>
                        </div>
                    )}

                    {/* Order ID */}
                    <div style={{
                        display: "flex", justifyContent: "space-between",
                        padding: "10px 14px",
                        backgroundColor: myCustomTheme.colors.header_footer_bg,
                        borderRadius: "8px",
                        border: `1px solid ${myCustomTheme.colors.high_light}`
                    }}>
                        <span style={{ color: myCustomTheme.colors.menu_text, fontSize: "13px" }}>Order ID</span>
                        <span style={{ fontWeight: "700", color: myCustomTheme.colors.heading, fontSize: "13px" }}>{currentOrderId}</span>
                    </div>

                    {/* Quantity (single product only) */}
                    {!isBuyAll && (
                        <div style={{
                            display: "flex", justifyContent: "space-between",
                            padding: "10px 14px",
                            backgroundColor: myCustomTheme.colors.header_footer_bg,
                            borderRadius: "8px",
                            border: `1px solid ${myCustomTheme.colors.high_light}`
                        }}>
                            <span style={{ color: myCustomTheme.colors.menu_text, fontSize: "13px" }}>Quantity</span>
                            <span style={{ fontWeight: "700", color: myCustomTheme.colors.heading, fontSize: "13px" }}>
                                {userDetails?.quantity || 1} unit{(userDetails?.quantity || 1) > 1 ? "s" : ""}
                            </span>
                        </div>
                    )}

                    {/* ── Price breakdown box ── */}
                    <div style={{
                        borderRadius: "10px",
                        border: `1.5px solid ${myCustomTheme.colors.high_light}`,
                        overflow: "hidden"
                    }}>

                        {/* Buy All: per-item price rows */}
                        {isBuyAll && cartItems.map((item) => (
                            <div
                                key={item.id}
                                style={{
                                    display: "flex", justifyContent: "space-between",
                                    padding: "9px 14px",
                                    backgroundColor: myCustomTheme.colors.header_footer_bg,
                                    borderBottom: `1px solid ${myCustomTheme.colors.high_light}`
                                }}
                            >
                                <span style={{ color: myCustomTheme.colors.menu_text, fontSize: "13px", flex: 1, marginRight: "8px" }}>
                                    {item.name}
                                    {item.type === "Customize" && (
                                        <span style={{ fontSize: "11px", color: "#94a3b8" }}> (6")</span>
                                    )}
                                    {item.type === "Customize" && cartImages[item.id] && (
                                        <span style={{ marginLeft: "5px", fontSize: "10px", color: "#15803d", fontWeight: "700" }}>✓ Design</span>
                                    )}
                                </span>
                                <span style={{ fontWeight: "700", color: myCustomTheme.colors.heading, fontSize: "13px", whiteSpace: "nowrap" }}>
                                    ₹{getItemDisplayPrice(item)}
                                </span>
                            </div>
                        ))}

                        {/* Product total row */}
                        <div style={{
                            display: "flex", justifyContent: "space-between",
                            padding: "10px 14px",
                            backgroundColor: myCustomTheme.colors.header_footer_bg,
                            borderBottom: `1px solid ${myCustomTheme.colors.high_light}`
                        }}>
                            <span style={{ color: myCustomTheme.colors.menu_text, fontSize: "13px" }}>
                                {isBuyAll ? `${cartItems.length} Items Total` : "Product Total"}
                                <span style={{ marginLeft: "6px", fontSize: "11px", color: "#15803d", fontWeight: "600" }}>(GST incl.)</span>
                            </span>
                            <span style={{ fontWeight: "700", color: myCustomTheme.colors.heading, fontSize: "13px" }}>₹{productTotal}</span>
                        </div>

                        {/* GST info row */}
                        <div style={{
                            display: "flex", justifyContent: "space-between",
                            padding: "8px 14px",
                            backgroundColor: "#fffbeb",
                            borderBottom: `1px solid ${myCustomTheme.colors.high_light}`
                        }}>
                            <span style={{ color: "#92400e", fontSize: "12px", display: "flex", alignItems: "center", gap: "5px" }}>
                                <FaShieldAlt style={{ fontSize: "11px" }} /> CGST 9% + SGST 9% (included)
                            </span>
                            <span style={{ fontWeight: "700", color: "#92400e", fontSize: "12px" }}>₹{cgst + sgst}</span>
                        </div>

                        {/* Shipping row */}
                        <div style={{
                            display: "flex", justifyContent: "space-between",
                            padding: "10px 14px",
                            backgroundColor: myCustomTheme.colors.header_footer_bg,
                            borderBottom: `1px solid ${myCustomTheme.colors.high_light}`
                        }}>
                            <span style={{ color: myCustomTheme.colors.menu_text, fontSize: "13px", display: "flex", alignItems: "center", gap: "5px" }}>
                                <FaTruck style={{ color: "#15803d", fontSize: "12px" }} />
                                Shipping
                                <span style={{ fontSize: "11px", color: "#64748b" }}>({shippingInfo.label})</span>
                            </span>
                            <span style={{
                                fontWeight: "700", fontSize: "13px",
                                color: shippingInfo.isFree ? "#15803d" : myCustomTheme.colors.heading
                            }}>
                                {shippingInfo.isFree ? "FREE" : `₹${shippingCharge}`}
                            </span>
                        </div>

                        {/* Grand total */}
                        <div style={{
                            display: "flex", justifyContent: "space-between",
                            padding: "13px 14px",
                            backgroundColor: "#0f172a"
                        }}>
                            <span style={{ color: "#f1f5f9", fontSize: "14px", fontWeight: "700" }}>Total Bill (Amount Payable)</span>
                            <span style={{ fontWeight: "800", color: "#ffffff", fontSize: "17px" }}>₹{grandTotal}</span>
                        </div>
                    </div>

                    {/* Payment method */}
                    <div style={{
                        display: "flex", justifyContent: "space-between",
                        padding: "10px 14px",
                        backgroundColor: myCustomTheme.colors.header_footer_bg,
                        borderRadius: "8px",
                        border: `1px solid ${myCustomTheme.colors.high_light}`
                    }}>
                        <span style={{ color: myCustomTheme.colors.menu_text, fontSize: "13px" }}>Payment</span>
                        <span style={{ fontWeight: "700", color: "#d97706", fontSize: "13px" }}>Cash on Delivery</span>
                    </div>
                </div>

                {/* ── Invoice download button ── */}
                <button
                    onClick={handleDownloadInvoice}
                    style={{
                        width: "100%",
                        padding: "14px",
                        backgroundColor: "#1e293b",
                        color: "#fff",
                        border: "none",
                        borderRadius: "12px",
                        fontSize: "15px",
                        fontWeight: "700",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: "10px",
                        marginBottom: "16px"
                    }}
                >
                    <FaFileInvoice /> Download Invoice (PDF)
                </button>

                {/* ── Company contact ── */}
                <div
                    className="company-contact"
                    style={{
                        borderTop: `1px dashed ${myCustomTheme.colors.high_light}`,
                        paddingTop: "16px",
                        marginTop: "4px"
                    }}
                >
                    <h3 style={{ color: myCustomTheme.colors.heading, fontSize: "14px", marginBottom: "8px" }}>
                        Company Contact
                    </h3>
                    <p style={{ color: myCustomTheme.colors.menu_text, fontSize: "13px", display: "flex", alignItems: "center", gap: "6px", flexWrap: "wrap" }}>
                        <FaPhoneAlt />
                        +91 9274457901
                        <FaMapMarkerAlt style={{ marginLeft: "8px" }} />
                        Hilton Business Centre
                        <FaEnvelope style={{ marginLeft: "8px" }} />
                        contact@necllp.com
                    </p>
                </div>
            </div>
        </div>
    );
};

export default OrderSuccess;